import { ArrowLeftOutlined, ExclamationCircleFilled, SyncOutlined } from "@ant-design/icons";
import { faRocket, faThLarge, faUsers } from "@fortawesome/free-solid-svg-icons";
import { Alert, Button, Tooltip, Typography } from "antd";
import React, { useState } from "react";
import { Link, useDataProvider, useEditContext, useNotify, usePermissions } from "react-admin";
import { ExtendedDataProvider, ServiceItemDetailProps, RoutableTabConfig } from '@src/types';
import { ResponsesTable } from "../Common/Integrations";
import { getTagForChangeState, getTagForResponsesStatus, getTagForRuntimeStates } from "../Common/Tags";
import VersionizedObjectsTab from "../Common/VersionizedObjectsTab";
import FaIcon from "../Icons/FaIcon";
import ChangeInstancesTab from "./SubComponents/ChangeInstancesTab";
import Declarations from "./SubComponents/Declarations";
import Metadata from "./SubComponents/Metadata";
import RoutableTabs from "../Common/RoutableTabs";
import MainCard from "../Common/MainCard";
import {
    CounterpartyName,
    CounterpartyPov,
    CounterpartyProvider,
    counterpartyOf,
} from "../Teams/Counterparty";
import { formatDateAndTime, formatDateAndTimeFromNow } from "@src/helpers/utils";
import PackPipelineDetail from "@src/components/AIProcessors/SubComponents/PackPipelineDetail";
import useGetGeneralSettings from "@src/providers/useGetGeneralSettings";
import { usePackAccess } from "@src/components/AIProcessors/packAccess";
import "./DetailView.css";

const { Text } = Typography;

/* A fact on the header's line, where there is room for the name but not for a
   label spelling out what kind of name it is — the icon carries that, and the
   tooltip spells it out along with the name in full.
   Names here are whatever someone typed, so the value is cut to the space it
   has rather than wrapping and taking the line's alignment with it. `children`
   is for an adornment that must stay visible however long the name is;
   `valueNode` is the name itself when it is more than text, as the
   counterparty's is. */
const InlineFact = ({
    icon,
    label,
    value,
    valueNode,
    children,
}: {
    icon: any;
    label: string;
    value?: string;
    valueNode?: React.ReactNode;
    children?: React.ReactNode;
}) => (
    <Tooltip title={`${label}: ${value || '-'}`}>
        <span className="sid-fact">
            <span className="sid-fact__ico" aria-hidden="true">{icon}</span>
            <span className="sid-sr">{label}: </span>
            <span className="sid-fact__value">{valueNode ?? value ?? '-'}</span>
            {children}
        </span>
    </Tooltip>
);

/* What this page calls the other party, on each side. The panel has a word of
   its own for it; this is the header's, and the service owner's was already
   here — the consumer's is its mirror. */
const COUNTERPARTY_LABEL: Record<CounterpartyPov, string> = {
    consumer: 'Consumer Team',
    serviceowner: 'Service Owner',
};

const ServiceItemDetailCard = ({ pov, serviceItemsResource, changeInstancesResource, deployedItemsResource, healthcheckResource, healthcheckResponsesResource }: ServiceItemDetailProps) => {
    const { record, loading } = useEditContext<any>();
    const [healthCheckTriggered, setHealthCheckTriggered] = useState(false)
    const dataProvider: ExtendedDataProvider = useDataProvider();
    const { permissions } = usePermissions();
    const notify = useNotify();
    const generalSettings = useGetGeneralSettings();
    const AIIntegrationSettings = generalSettings.find(setting => setting.name == 'AIIntegration')

    /* Whether the Pack tab belongs on this page at all, and whether it can be
       driven from it. One answer for both points of view — see packAccess.ts.
       Hooks run before the record does, hence the optional chaining; the guard
       below keeps the tabs from being built until the record is in. */
    const packAccess = usePackAccess({
        pov,
        permissions,
        aiIntegrationEnabled: !!AIIntegrationSettings?.config?.show_ai_processor,
        hasAiProcessors: !!record?.has_ai_processors,
        serviceId: record?.service?.id,
    });

    const triggerHealthcheckForServiceItem = (record: any) => {
        setHealthCheckTriggered(true);
        dataProvider.getAction(healthcheckResource, { verb: "trigger/service_item/" + record.id })
            .then((response: any) => {
                notify('Healthcheck triggered successfully')
                setHealthCheckTriggered(false)
            }).catch((error: any) => {
                // The API says why it refused — the healthcheck is paused, or
                // its rate limit is spent — and that reason is the only signal
                // this page has: the service carries `healthcheck` as a bare
                // boolean, so the button cannot know the check is switched off.
                notify(error.body?.trigger || 'Error triggering healthcheck', 'error')
                setHealthCheckTriggered(false)
            })
    }

    const getMessageForServiceState = (state: string) => {
        if (state === "NO_NEW_SERVICE_ITEM")
            return <>The Service {<Text strong>{record?.service?.name}</Text>} is no longer accepting new Service Items. Consumers can still modify or delete existing Service Items.</>

        else if (state === "NO_MODIFY_EXISTING_SERVICE_ITEM")
            return <>Consumers can only decommission existing service items for the {<Text strong>{record?.service?.name}</Text>} service. Creation and modification are not allowed.</>

        else if (state === "DECOMMISSIONED")
            return <>The Service {<Text strong>{record?.service?.name}</Text>} has been fully decommissioned and is no longer available.</>
    }

    const permissionChangeState = ["ADMIN", "GENERAL"].includes(permissions);
    const backTo = pov === "serviceowner" ? "/owner_service_items" : "/service_items";

    if (loading || !record) return null;

    /* The team on the other side, which is the side the reader is not on. This
       is the detail read rather than the light list one, so `service_owner_team`
       is there; `service.owner` is the same team by another route, kept as the
       fallback the lists fall back to. */
    const counterpartyPov = counterpartyOf(pov);
    const counterparty = counterpartyPov && {
        pov: counterpartyPov,
        team: counterpartyPov === 'consumer'
            ? record.consumer_team
            : record.service_owner_team ?? record.service?.owner,
    };

    const tabs: RoutableTabConfig[] = [
        {
            key: 'declarations',
            tab: "Declarations",
            content: <Declarations resource={serviceItemsResource} service_item={record} />,
        },
        {
            key: 'change_instances',
            tab: "Change Instances",
            content: (
                <ChangeInstancesTab
                    resource={changeInstancesResource}
                    baseFilter={{ service_item_id: record.id }}
                    submissionPageRedirectVisible={pov === "consumer"}
                />
            ),
        },
        {
            key: 'deployed_items',
            tab: "Deployed Items",
            content: (
                <VersionizedObjectsTab
                    resource={deployedItemsResource}
                    light={true}
                    name={"deployed item"}
                    attrName={"data"}
                    isCreate={pov === "serviceowner" && permissions === "ADMIN"}
                    record={record}
                    pov={pov}
                />
            ),
        },
        {
            key: 'metadata',
            tab: "Metadata",
            content: <Metadata record={record} />,
        },
    ];

    if (record?.service?.healthcheck)
        tabs.push({
            key: 'healthcheck_responses',
            tab: "Healthcheck Responses",
            content: (
                // The header above names the item and its service, so the log
                // carries neither: what is left is the response itself — what
                // came back, what state it is in, and when.
                <ResponsesTable
                    config={{
                        resource: healthcheckResponsesResource,
                        emptyText: "This item has not been checked yet.",
                    }}
                    filter={{ service_item_id: record.id }}
                />
            ),
        })

    if (packAccess.visible)
        tabs.push({
            key: 'netorca_pack',
            tab: "NetOrca Pack",
            content: (
                <PackPipelineDetail
                    service_item_id={record.id}
                    service_id={record.service?.id}
                    pov={pov}
                    readOnly={packAccess.readOnly}
                />
            ),
        })

    return (
        // the header names one counterparty, and the panel opens over the page
        <CounterpartyProvider>
        <div className="netorca-sid netorca-frame">
            {record.is_service_private && (
                <Alert
                    className="sid-alert"
                    message="Service has become private"
                    description="The service has become private and your team is not in the service's allowed teams list"
                    type="error"
                    showIcon
                />
            )}
            {record.service && record.service.state !== "IN_SERVICE" && (
                <Alert
                    className="sid-alert"
                    message={getMessageForServiceState(record.service.state)}
                    type="warning"
                    showIcon
                />
            )}

            {/* Identity header */}
            <header className="sid-header">
                {/* Back, then the name, then everything else beside it — the arrow
                    alone says where it goes back to, and the page has said the
                    word "service item" enough by the time you are on one. */}
                <div className="sid-header__main">
                    {/* The name and what it belongs to share a line, and give way
                        together: when the names outgrow it they wrap below the
                        name, while the states hold the end of the first line. */}
                    <div className="sid-lead">
                        <div className="sid-identity">
                            <Tooltip title="Back to service items">
                                <Link className="sid-back" to={backTo} aria-label="Back to service items">
                                    <ArrowLeftOutlined aria-hidden="true" />
                                </Link>
                            </Tooltip>
                            {/* cut to the line rather than wrapping onto a second
                                one; the tooltip has the name in full */}
                            <Tooltip placement="bottomLeft" title={record.name}>
                                <h1 className="sid-title">{record.name || "Untitled service item"}</h1>
                            </Tooltip>
                        </div>

                        <div className="sid-facts">
                            {/* Whoever is on the other side of this item, on
                                both sides: the owner's consumer was already
                                named here, and now that the API will hand over
                                a counterparty's team, the consumer's owner is
                                worth naming too. Either opens the team. */}
                            {counterparty && (
                                <InlineFact
                                    icon={FaIcon(faUsers)}
                                    label={COUNTERPARTY_LABEL[counterparty.pov]}
                                    value={counterparty.team?.name}
                                    valueNode={
                                        <CounterpartyName
                                            team={counterparty.team}
                                            pov={counterparty.pov}
                                            label={COUNTERPARTY_LABEL[counterparty.pov]}
                                            fallback="-"
                                        />
                                    }
                                />
                            )}
                            <InlineFact
                                icon={FaIcon(faThLarge)}
                                label="Application"
                                value={record.application?.name}
                            />
                            <InlineFact
                                icon={FaIcon(faRocket)}
                                label="Service"
                                value={record.service?.name}
                            >
                                {record.service?.name && record.is_deprecated_service_schema && (
                                    <Tooltip title="The service has a new schema" color="orange" placement="topRight">
                                        <ExclamationCircleFilled
                                            className="sid-fact__warn"
                                            aria-label="The service has a new schema"
                                        />
                                    </Tooltip>
                                )}
                            </InlineFact>
                        </div>
                    </div>

                    {/* What the item is right now, held at the end of the card:
                        its states on the first line and, under them, the health
                        the service last reported. */}
                    <div className="sid-header__aside">
                        <div className="sid-header__states">
                            {getTagForRuntimeStates(record.runtime_state)}
                            {getTagForChangeState(record.change_state)}
                            {record.created && (
                                <Tooltip title={formatDateAndTime(record.created)}>
                                    <span className="sid-created">
                                        Created {formatDateAndTimeFromNow(record.created)}
                                    </span>
                                </Tooltip>
                            )}
                        </div>

                        {record?.service?.healthcheck && (
                            <div className="sid-health">
                                <span className="sid-health__label">Healthcheck</span>
                                <Tooltip title="Last Healthcheck Response">
                                    {getTagForResponsesStatus(record.healthcheck_status)}
                                </Tooltip>
                                {permissionChangeState && (
                                    <Tooltip title="Trigger Healthcheck">
                                        <Button
                                            size="small"
                                            aria-label="Trigger healthcheck"
                                            loading={healthCheckTriggered}
                                            onClick={() => triggerHealthcheckForServiceItem(record)}
                                            icon={<SyncOutlined />}
                                        >
                                            Run
                                        </Button>
                                    </Tooltip>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </header>

            <MainCard className="sid-panel">
                <RoutableTabs className="sid-tabs" tabs={tabs} defaultActiveKey="declarations" />
            </MainCard>
        </div>
        </CounterpartyProvider>
    )
}

export default ServiceItemDetailCard;
